import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { NgxMaskPipe } from 'ngx-mask';
import { forkJoin, of } from 'rxjs';
import { FinanceiroService } from '../../../../services/financeiro.service';
import { InadimplentesDTO } from '../../../../models/financeiro.model';
import { formatarMoedaBRL } from '../../../../core/utils/moeda.util';

@Component({
  selector: 'app-inadimplentes',
  standalone: true,
  imports: [CommonModule, NgxMaskPipe],
  templateUrl: './inadimplentes.component.html',
  styleUrls: ['./inadimplentes.component.css']
})
export class InadimplentesComponent implements OnInit {
  inadimplentes: InadimplentesDTO[] = [];
  currentPage = 0;
  itemsPerPage = 10;
  totalPages = 1;
  totalElements = 0;

  maxPagesToShow = 5;
  startPage = 0;
  endPage = 0;
  pages: number[] = [];

  private cdr = inject(ChangeDetectorRef);

  constructor(
    private financeiroService: FinanceiroService,
    private router: Router,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadInadimplentes();
  }

  loadInadimplentes(): void {
    this.financeiroService.getInadimplentes(this.currentPage, this.itemsPerPage).subscribe({
      next: (data) => {
        this.inadimplentes = data.content;
        this.totalPages = data.totalPages;
        this.totalElements = data.totalElements;
        this.updatePageNumbers();
        this.cdr.detectChanges();
      },
      error: () => {
        this.toastr.error('Erro ao carregar inadimplentes.');
      }
    });
  }

  setPage(page: number): void {
    if (page < 0 || page >= this.totalPages) return;
    this.currentPage = page;
    this.loadInadimplentes();
  }

  updatePageNumbers(): void {
    const halfPages = Math.floor(this.maxPagesToShow / 2);
    this.startPage = Math.max(0, this.currentPage - halfPages);
    this.endPage = Math.min(this.totalPages - 1, this.startPage + this.maxPagesToShow - 1);

    if (this.endPage - this.startPage + 1 < this.maxPagesToShow) {
      this.startPage = Math.max(0, this.endPage - this.maxPagesToShow + 1);
    }

    this.pages = Array.from(
      { length: this.endPage - this.startPage + 1 },
      (_, i) => this.startPage + i
    );
  }

  verVendasCliente(idCliente: number): void {
    this.router.navigate(['/home', 'cadastro-cliente-vendas', idCliente]);
  }

   verParcelasAbertas(idVenda: number): void {
    this.router.navigate(['/home', 'financeiro-conta-a-receber-manutencao', idVenda]);
  }

  imprimir(): void {
    if (this.totalPages === 0) {
      this.toastr.warning('Não há dados para imprimir.');
      return;
    }

    // Se há apenas uma página carregada, usa os dados em memória diretamente
    if (this.totalPages === 1) {
      this.gerarImpressao(this.inadimplentes);
      return;
    }

    // Busca todas as páginas em paralelo para garantir todos os registros
    const requests = Array.from({ length: this.totalPages }, (_, i) =>
      this.financeiroService.getInadimplentes(i, this.itemsPerPage)
    );

    forkJoin(requests).subscribe({
      next: (pages) => {
        const todos = pages.flatMap(p => p.content);
        this.gerarImpressao(todos);
      },
      error: () => {
        this.toastr.error('Erro ao carregar dados para impressão.');
      }
    });
  }

  private gerarImpressao(inadimplentes: InadimplentesDTO[]): void {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      this.toastr.warning('Não foi possível abrir a janela de impressão. Verifique se o bloqueador de pop-up está ativo.');
      return;
    }

    const esc = (value: string | number | null | undefined): string =>
      String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const formatCurrency = (value: number): string => formatarMoedaBRL(value ?? 0);

    const formatDoc = (doc: string): string => {
      const d = String(doc ?? '').replace(/\D/g, '');
      if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
      return esc(doc);
    };

    const formatPhone = (value: string): string => {
      const digits = String(value ?? '').replace(/\D/g, '');
      if (digits.length === 11) return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
      if (digits.length === 10) return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
      return esc(value);
    };

    const formatDate = (value: string): string => {
      if (!value) return '-';
      const [ano, mes, dia] = value.split('-');
      return `${dia}/${mes}/${ano}`;
    };

    const rows = inadimplentes.map((item, i) => `
      <tr class="${i % 2 === 0 ? 'even' : 'odd'}">
        <td>${esc(item.nomeRazaoSocial)}</td>
        <td class="center">${formatDoc(item.cpfCnpj)}</td>
        <td class="center">${formatPhone(item.telefone)}</td>
        <td class="right">${esc(formatCurrency(item.valorEmAberto))}</td>
        <td class="center">${esc(item.qtdDebitosVencidos)}</td>
        <td class="center">${formatDate(item.primeiroVencimento)}</td>
      </tr>`).join('');

    const totalGeral = inadimplentes.reduce((acc, i) => acc + (i.valorEmAberto ?? 0), 0);
    const today = new Date().toLocaleDateString('pt-BR');
    const logoUrl = `${window.location.origin}/assets/logo/logo_transparente.png`;

    const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8" />
  <title>Inadimplentes</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 10pt; color: #000; background: #fff; }
    @page { size: A4 landscape; margin: 10mm; }
    .header { display: flex; align-items: center; justify-content: space-between; border-bottom: 2px solid #334155; padding-bottom: 8px; margin-bottom: 12px; }
    .header img { height: 48px; }
    .header-title { text-align: center; }
    .header-title h1 { font-size: 15pt; font-weight: bold; }
    .header-title p { font-size: 9pt; color: #555; margin-top: 2px; }
    .header-date { font-size: 9pt; color: #555; text-align: right; }
    table { width: 100%; border-collapse: collapse; margin-top: 8px; }
    thead th { background: #334155; color: #fff; padding: 6px 8px; font-size: 9pt; text-transform: uppercase; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    tbody tr.even { background: #f8fafc; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    td { padding: 5px 8px; border-bottom: 1px solid #e2e8f0; font-size: 9pt; }
    .center { text-align: center; }
    .right { text-align: right; }
    .footer { margin-top: 12px; display: flex; justify-content: flex-end; border-top: 1px solid #e2e8f0; padding-top: 8px; font-size: 10pt; font-weight: bold; }
    .footer span { margin-left: 16px; color: #dc2626; }
  </style>
</head>
<body>
  <div class="header">
    <img src="${logoUrl}" alt="Logo" onerror="this.style.display='none'" />
    <div class="header-title">
      <h1>Relatório de Inadimplentes</h1>
      <p>Total de ${esc(inadimplentes.length)} registros</p>
    </div>
    <div class="header-date">Emitido em: ${today}</div>
  </div>
  <table>
    <thead>
      <tr>
        <th>Nome / Razão Social</th>
        <th class="center">CPF/CNPJ</th>
        <th class="center">Telefone</th>
        <th class="right">Valor em Aberto</th>
        <th class="center">Débitos Vencidos</th>
        <th class="center">Primeiro Vencimento</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
    <tfoot>
      <tr>
        <td colspan="3" class="right" style="font-weight:bold; border-top:2px solid #000;">Total em Aberto:</td>
        <td class="right" style="font-weight:bold; border-top:2px solid #000; color:#dc2626;">${esc(formatCurrency(totalGeral))}</td>
        <td colspan="2" style="border-top:2px solid #000;"></td>
      </tr>
    </tfoot>
  </table>
  <script>window.onload = function() { window.print(); };<\/script>
</body>
</html>`;

    printWindow.document.write(html);
    printWindow.document.close();
  }

  formatarData(data: string): string {
    if (!data) return '-';
    const [ano, mes, dia] = data.split('-');
    return `${dia}/${mes}/${ano}`;
  }
}
